/**
 * FactoryJourneyErrors (AJIP-16.02.009 · §26)
 * ---------------------------------------------------------------------
 * Typed error hierarchy. Every Factory Journey failure returns or
 * throws one of these subclasses. §26 outcome codes are carried on the
 * `outcomeCode` property where semantics require an audit-stable code.
 */
import { JourneyError } from '../core/JourneyErrors';

class FactoryJourneyError extends JourneyError {
  constructor(message, outcomeCode = null) {
    super(message);
    this.outcomeCode = outcomeCode;
  }
}
class InvalidFactoryContextError extends FactoryJourneyError {}
class InvalidFactoryStageError extends FactoryJourneyError {}
class InvalidFactoryTransitionError extends FactoryJourneyError {}
class FactoryJourneyNotActiveError extends FactoryJourneyError {}
class FactoryInstanceIdentityError extends FactoryJourneyError {}
class FactoryOwnershipAuthorityError extends FactoryJourneyError {}
class FactoryCommandInvalidError extends FactoryJourneyError {}
class FactoryCommandDuplicateError extends FactoryJourneyError {}
class FactoryStateConflictError extends FactoryJourneyError {}
class FactorySubjectMismatchError extends FactoryJourneyError {}
class FactoryActorUnauthorisedError extends FactoryJourneyError {}
class FactoryRequirementsIncompleteError extends FactoryJourneyError {}
class BuyerFactoryBoundaryError extends FactoryJourneyError {}
class SupplierFactoryBoundaryError extends FactoryJourneyError {}

const FACTORY_JOURNEY_ERROR_TYPES = Object.freeze([
  'FactoryJourneyError',
  'InvalidFactoryContextError',
  'InvalidFactoryStageError',
  'InvalidFactoryTransitionError',
  'FactoryJourneyNotActiveError',
  'FactoryInstanceIdentityError',
  'FactoryOwnershipAuthorityError',
  'FactoryCommandInvalidError',
  'FactoryCommandDuplicateError',
  'FactoryStateConflictError',
  'FactorySubjectMismatchError',
  'FactoryActorUnauthorisedError',
  'FactoryRequirementsIncompleteError',
  'BuyerFactoryBoundaryError',
  'SupplierFactoryBoundaryError',
]);

// §26 outcome codes — audit-stable strings for evidence and testing.
const FACTORY_OUTCOME_CODES = Object.freeze({
  FACTORY_JOURNEY_DISABLED: 'FACTORY_JOURNEY_DISABLED',
  FACTORY_SUBJECT_NOT_FOUND: 'FACTORY_SUBJECT_NOT_FOUND',
  FACTORY_SUBJECT_INVALID: 'FACTORY_SUBJECT_INVALID',
  FACTORY_ACTOR_UNAUTHORISED: 'FACTORY_ACTOR_UNAUTHORISED',
  FACTORY_RELATIONSHIP_UNCONFIRMED: 'FACTORY_RELATIONSHIP_UNCONFIRMED',
  FACTORY_JOURNEY_NOT_FOUND: 'FACTORY_JOURNEY_NOT_FOUND',
  FACTORY_JOURNEY_ALREADY_ACTIVE: 'FACTORY_JOURNEY_ALREADY_ACTIVE',
  FACTORY_JOURNEY_ALREADY_COMPLETED: 'FACTORY_JOURNEY_ALREADY_COMPLETED',
  FACTORY_JOURNEY_VERSION_UNSUPPORTED: 'FACTORY_JOURNEY_VERSION_UNSUPPORTED',
  FACTORY_STAGE_INVALID: 'FACTORY_STAGE_INVALID',
  FACTORY_TRANSITION_NOT_PERMITTED: 'FACTORY_TRANSITION_NOT_PERMITTED',
  FACTORY_REQUIREMENTS_INCOMPLETE: 'FACTORY_REQUIREMENTS_INCOMPLETE',
  FACTORY_STATE_CONFLICT: 'FACTORY_STATE_CONFLICT',
  FACTORY_COMMAND_DUPLICATE: 'FACTORY_COMMAND_DUPLICATE',
  FACTORY_COMMAND_INVALID: 'FACTORY_COMMAND_INVALID',
  FACTORY_PERSISTENCE_UNAVAILABLE: 'FACTORY_PERSISTENCE_UNAVAILABLE',
  FACTORY_ACTIVATION_DENIED: 'FACTORY_ACTIVATION_DENIED',
  FACTORY_OPERATION_FAILED: 'FACTORY_OPERATION_FAILED',
});

export {
  FactoryJourneyError,
  InvalidFactoryContextError,
  InvalidFactoryStageError,
  InvalidFactoryTransitionError,
  FactoryJourneyNotActiveError,
  FactoryInstanceIdentityError,
  FactoryOwnershipAuthorityError,
  FactoryCommandInvalidError,
  FactoryCommandDuplicateError,
  FactoryStateConflictError,
  FactorySubjectMismatchError,
  FactoryActorUnauthorisedError,
  FactoryRequirementsIncompleteError,
  BuyerFactoryBoundaryError,
  SupplierFactoryBoundaryError,
  FACTORY_JOURNEY_ERROR_TYPES,
  FACTORY_OUTCOME_CODES,
};
